import React, { useState } from 'react';
import { connect } from "react-redux";
import { selectAuthState } from "../../../store/selectors/authSelectors";
import { Search, Bot, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { Button, Input, Toaster, useToast } from "@mantis-sas/ui";
import { getWorkflowResultsByIdP2O } from "../../../ApiCalls/mantisFlowsManangment";

function Outlets(props) {
  const [iprId, setIprId] = useState('');
  const [loading, setLoading] = useState(false);
  const [outletsData, setOutletsData] = useState({
    extract: null,
    general: null,
    verification: null
  });
  const { toast } = useToast();

  // Función para obtener las respuestas de los agentes de IA
  const fetchOutletsData = async (searchIprId) => {
    if (!searchIprId.trim()) {
      toast({
        title: "Error",
        description: "Por favor ingresa un ID de IPR válido",
        variant: "destructive"
      });
      return;
    }

    // Verificar que tenemos clientId disponible
    if (!props.mantisBusiness?._id) {
      toast({
        title: "Error",
        description: "No se pudo obtener el ID del cliente",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      // Obtener datos del endpoint real
      const clientId = props.mantisBusiness._id;
      const response = await getWorkflowResultsByIdP2O(clientId, searchIprId);
      
      const iprData = response.data && response.data[0] ? response.data[0] : null;
      
      const outletsData = {
        extract: {
          title: "IPR CREATION",
          status: "completed",
          data: response, // Toda la data completa
          summary: "Datos completos del IPR obtenidos exitosamente"
        },
        general: {
          title: "IPR APPROVAL PLAN",
          status: iprData && iprData.approval_plan ? "completed" : "pending",
          data: iprData ? iprData.approval_plan : null,
          summary: iprData && iprData.approval_plan ? "Plan de aprobación disponible" : "No hay plan de aprobación disponible"
        },
        verification: {
          title: "IPR INSIGHTS",
          status: iprData && iprData.ia_insights ? "completed" : "pending",
          data: iprData ? iprData.ia_insights : null,
          summary: iprData && iprData.ia_insights ? "Insights de IA disponibles" : "No hay insights de IA disponibles"
        }
      };

      setOutletsData(outletsData);
      
      toast({
        title: "Datos cargados",
        description: `Respuestas de agentes obtenidas para IPR: ${searchIprId}`,
        variant: "default"
      });

    } catch (error) {
      console.error("Error fetching outlets data:", error);
      toast({
        title: "Error",
        description: "No se pudieron obtener las respuestas de los agentes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchOutletsData(iprId);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'pending':
        return <AlertCircle className="h-5 w-5 text-blue-500" />;
      default:
        return <Bot className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20';
      case 'pending':
        return 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20';
      default:
        return 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800';
    }
  };

  const renderExtractOutlet = () => {
    const data = outletsData.extract;
    if (!data) return null;

    return (
      <div className={`rounded-xl border-2 p-6 ${getStatusColor(data.status)}`}>
        <div className="flex items-center space-x-3 mb-4">
          {getStatusIcon(data.status)}
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {data.title}
          </h3>
        </div>
        
        <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
          {data.data && typeof data.data === 'object' ? (
            <div className="p-3 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
              <pre className="text-sm text-slate-900 dark:text-slate-100 whitespace-pre-wrap">
                {JSON.stringify(data.data, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="p-3 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
              <p className="text-slate-900 dark:text-slate-100">
                {data.data || 'No hay datos disponibles'}
              </p>
            </div>
          )}
        </div>
        
        <p className="text-sm text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 p-3 rounded border border-slate-200 dark:border-slate-700">
          {data.summary}
        </p>
      </div>
    );
  };

  const renderGeneralOutlet = () => {
    const data = outletsData.general;
    if (!data) return null;

    return (
      <div className={`rounded-xl border-2 p-6 ${getStatusColor(data.status)}`}>
        <div className="flex items-center space-x-3 mb-4">
          {getStatusIcon(data.status)}
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {data.title}
          </h3>
        </div>
        
        <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
          {data.data ? (
            <div className="p-3 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
              <div className="font-medium text-slate-700 dark:text-slate-300 mb-2">
                Plan de Aprobación
              </div>
              <div className="text-slate-900 dark:text-slate-100">
                {typeof data.data === 'object' ? (
                  <pre className="text-sm whitespace-pre-wrap">
                    {JSON.stringify(data.data, null, 2)}
                  </pre>
                ) : (
                  data.data
                )}
              </div>
            </div>
          ) : (
            <div className="p-3 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
              <p className="text-slate-900 dark:text-slate-100">
                No hay plan de aprobación disponible
              </p>
            </div>
          )}
        </div>
        
        <p className="text-sm text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 p-3 rounded border border-slate-200 dark:border-slate-700">
          {data.summary}
        </p>
      </div>
    );
  };

  const renderVerificationOutlet = () => {
    const data = outletsData.verification;
    if (!data) return null;

    return (
      <div className={`rounded-xl border-2 p-6 ${getStatusColor(data.status)}`}>
        <div className="flex items-center space-x-3 mb-4">
          {getStatusIcon(data.status)}
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {data.title}
          </h3>
        </div>
        
        <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
          {data.data ? (
            <div className="p-3 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
              <div className="font-medium text-slate-700 dark:text-slate-300 mb-2">
                IA Insights
              </div>
              <div className="text-slate-900 dark:text-slate-100">
                {typeof data.data === 'object' ? (
                  <pre className="text-sm whitespace-pre-wrap">
                    {JSON.stringify(data.data, null, 2)}
                  </pre>
                ) : (
                  data.data
                )}
              </div>
            </div>
          ) : (
            <div className="p-3 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
              <p className="text-slate-900 dark:text-slate-100">
                No hay insights de IA disponibles
              </p>
            </div>
          )}
        </div>
        
        <p className="text-sm text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 p-3 rounded border border-slate-200 dark:border-slate-700">
          {data.summary}
        </p>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6 min-h-screen bg-gradient-to-br from-slate-50/50 via-white to-blue-50/30 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl shadow-sm">
          <Bot className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            Outputs de Agentes IA
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Consulta las respuestas de los agentes de IA para un IPR específico
          </p>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-white/70 dark:bg-slate-800/70 backdrop-blur-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-sm p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Ingresa el Order ID del IPR..."
              value={iprId}
              onChange={(e) => setIprId(e.target.value)}
              onKeyPress={handleKeyPress}
              className="h-12 text-lg"
            />
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
              El Client ID se obtiene automáticamente desde tu perfil de negocio
            </p>
          </div>
          <Button
            onClick={handleSearch}
            disabled={loading}
            className="h-12 px-6 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <Search className="h-5 w-5 mr-2" />
            )}
            {loading ? 'Cargando...' : 'Buscar'}
          </Button>
        </div>
      </div>

      {/* Results Section */}
      {(outletsData.extract || outletsData.general || outletsData.verification) && (
        <div className="grid gap-6 lg:grid-cols-1 xl:grid-cols-3">
          {/* Extract Agent */}
          <div className="space-y-4">
            {renderExtractOutlet()}
          </div>

          {/* General Agent */}
          <div className="space-y-4">
            {renderGeneralOutlet()}
          </div>

          {/* Verification Agent */}
          <div className="space-y-4">
            {renderVerificationOutlet()}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!outletsData.extract && !outletsData.general && !outletsData.verification && !loading && (
        <div className="text-center py-12">
          <Bot className="mx-auto h-16 w-16 text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            No hay datos para mostrar
          </h3>
          <p className="text-slate-600 dark:text-slate-400">
            Ingresa un ID de IPR para ver las respuestas de los agentes de IA
          </p>
        </div>
      )}

      <Toaster />
    </div>
  );
}

export default connect(selectAuthState)(Outlets); 
